import React, { useState, useMemo, useEffect } from "react";
import {
  Search,
  Phone,
  MapPin,
  Navigation,
  ArrowLeft,
  ArrowRight,
} from "lucide-react";
import { menuData } from "./MenuData/veerjiMenuData";
import { carouselImages } from "./MenuData/coresol";

const VeerJiMenu = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const categories = ["all", ...Object.keys(menuData)];

  // Auto-advance carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide(
      (prev) => (prev - 1 + carouselImages.length) % carouselImages.length,
    );
  };

  // Touch handlers for swipe
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      nextSlide();
    }
    if (isRightSwipe) {
      prevSlide();
    }

    setTouchStart(0);
    setTouchEnd(0);
  };

  const filteredMenu = useMemo(() => {
    let filtered = {};

    Object.keys(menuData).forEach((category) => {
      const items = menuData[category].filter((item) =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()),
      );

      if (items.length > 0) {
        filtered[category] = items;
      }
    });

    if (selectedCategory !== "all") {
      filtered = { [selectedCategory]: filtered[selectedCategory] || [] };
    }

    return filtered;
  }, [searchTerm, selectedCategory]);

  const handleLocationClick = () => {
    window.open(
      "https://www.google.com/maps/dir//Veer+Ji+Malai+Chaap+Wale+(Air+Force+Station,+Hindon),+Air+force+station+hindon+chowk,+opposite+police+chowki,+Karera,+Mohan+Nagar,+Sahibabad,+Ghaziabad,+Uttar+Pradesh+201007/@28.6883493,77.2992489,13z/data=!4m17!1m8!3m7!1s0x390cfb2eacbb3b43:0xd5406e63e70b9bb1!2sVeer+Ji+Malai+Chaap+Wale+(Air+Force+Station,+Hindon)!8m2!3d28.6883493!4d77.3754666!15sCiR2ZWVyIGppIG1hbGFpIGNoYWFwIHdhbGUgbW9oYW4gbmFnYXJaJiIkdmVlciBqaSBtYWxhaSBjaGFhcCB3YWxlIG1vaGFuIG5hZ2FykgEKcmVzdGF1cmFudOABAA!16s%2Fg%2F11lw38jxxx!4m7!1m0!1m5!1m1!1s0x390cfb2eacbb3b43:0xd5406e63e70b9bb1!2m2!1d77.3754666!2d28.6883493?entry=ttu&g_ep=EgoyMDI2MDEyOC4wIKXMDSoASAFQAw%3D%3D",
      "_blank",
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black text-white shadow-lg">
        <div className="max-w-screen-xl mx-auto px-4">
          <div className="flex items-center justify-between gap-4 py-3">
            {/* Left Side - Logo & Title */}
            <div className="flex items-center gap-1 flex-1 min-w-0">
              <img
                src="preview.png"
                alt="Veer Ji Logo"
                className="w-10 h-10 object-contain flex-shrink-0"
              />
              <div className="flex flex-col min-w-0 flex-1">
                <h1
                  className="text-2xl font-bold leading-none m-0"
                  style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
                >
                  Veer Ji
                </h1>
                <p className="text-sm text-gray-400 mt-1 leading-none">
                  Malai Chaap Wale • Veg 
                </p>
              </div>
            </div>

            {/* Right Side - Phone Icons */}
            <div className="flex items-center gap-4 flex-shrink-0">
              <a
                href="tel:8860866540"
                className="text-white transition-transform active:scale-95"
                aria-label="Call 8860866540"
              >
                <Phone className="w-6 h-6" />
              </a>
              <a
                href="tel:9643580306"
                className="text-white transition-transform active:scale-95"
                aria-label="Call 9643580306"
              >
                <Phone className="w-6 h-6" />
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Carousel */}
      <section
        className="relative w-full h-[300px] sm:h-[350px] md:h-[450px] lg:h-[500px] overflow-hidden bg-black"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="relative w-full h-full">
          {/* Slides Container */}
          <div className="relative w-full h-full overflow-hidden">
            {carouselImages.map((image, index) => (
              <div
                key={index}
                className={`absolute top-0 left-0 w-full h-full transition-all duration-500 ease-in-out ${
                  index === currentSlide
                    ? "opacity-100 translate-x-0"
                    : index < currentSlide
                      ? "opacity-0 -translate-x-full"
                      : "opacity-0 translate-x-full"
                }`}
              >
                <img
                  src={image.url}
                  alt={image.alt}
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                  <h3 className="text-xl md:text-2xl lg:text-3xl font-bold text-white drop-shadow-lg">
                    {image.alt}
                  </h3>
                </div>
              </div>
            ))}
          </div>

          {/* Previous Button */}
          <button
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white active:bg-white rounded-full w-10 h-10 lg:w-12 lg:h-12 flex items-center justify-center transition-all active:scale-95 z-10 touch-manipulation"
            onClick={prevSlide}
            aria-label="Previous slide"
          >
            <ArrowLeft size={20} className="text-black" />
          </button>

          {/* Next Button */}
          <button
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white active:bg-white rounded-full w-10 h-10 lg:w-12 lg:h-12 flex items-center justify-center transition-all active:scale-95 z-10 touch-manipulation"
            onClick={nextSlide}
            aria-label="Next slide"
          >
            <ArrowRight size={20} className="text-black" />
          </button>

          {/* Indicators */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
            {carouselImages.map((_, index) => (
              <button
                key={index}
                className={`rounded-full transition-all cursor-pointer touch-manipulation ${
                  currentSlide === index
                    ? "w-3 h-3 bg-white"
                    : "w-2.5 h-2.5 bg-white/50 hover:bg-white/75 active:bg-white/75"
                }`}
                onClick={() => setCurrentSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      

      {/* Location Bar */}
      <div className="bg-gray-900">
        <div className="max-w-screen-xl mx-auto px-4">
          <div className="py-4">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 text-sm font-medium text-white">
              <div
                className="flex items-center gap-2 text-center"
                onClick={handleLocationClick}
                style={{ cursor: "pointer" }}
              >
                <MapPin className="w-4 h-4 flex-shrink-0" />
                <span>Karhera Colony Near Air Force Hindon</span>
              </div>
              <div className="hidden sm:block text-white text-2xl">•</div>
              <div
                className="flex items-center gap-2 text-center"
                onClick={handleLocationClick}
                style={{ cursor: "pointer" }}
              >
                <MapPin className="w-4 h-4 flex-shrink-0" />
                <span>Kanha Complex Sec-2 Rajender Nagar</span>
              </div>
            </div>
          </div>
        </div>
      </div>{/* Delivery Info */}
      <div className="bg-black py-3 text-center">
        <div className="max-w-screen-xl mx-auto px-4">
          <p className="text-white font-semibold text-sm md:text-base">
            🚚 Free Home Delivery on Orders Above ₹250
          </p>
        </div>
      </div>
{/* Party Facilities Section */}
      <div className="bg-white py-8">
        <div className="max-w-screen-xl mx-auto px-4">
          <h2
            className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-6"
            style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
          >
            Perfect Venue for Your Celebrations
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Kitty Party */}
            <div className="relative overflow-hidden rounded-lg shadow-lg group">
              <img
                src="https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=400&h=300&fit=crop"
                alt="Kitty Party"
                className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end">
                <h3 className="text-white text-xl font-bold p-4">
                  Kitty Party
                </h3>
              </div>
            </div>

            {/* Office Party */}
            <div className="relative overflow-hidden rounded-lg shadow-lg group">
              <img
                src="https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=400&h=300&fit=crop"
                alt="Office Party"
                className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end">
                <h3 className="text-white text-xl font-bold p-4">
                  Office Party
                </h3>
              </div>
            </div>

            {/* Birthday Party */}
            <div className="relative overflow-hidden rounded-lg shadow-lg group">
              <img
                src="https://images.unsplash.com/photo-1558636508-e0db3814bd1d?w=400&h=300&fit=crop"
                alt="Birthday Party"
                className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end">
                <h3 className="text-white text-xl font-bold p-4">
                  Birthday Party
                </h3>
              </div>
            </div>
          </div>
        </div>
      </div>
      

      {/* Search and Filter */}
      <div className="bg-white shadow-sm py-6 md:py-8">
        <div className="max-w-screen-xl mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="flex flex-col gap-4">
              {/* Search Bar */}
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search for your favorite dish..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full py-3.5 pl-12 pr-4 border-2 border-gray-200 rounded-lg bg-gray-50 text-gray-900 text-[15px] outline-none transition-all focus:border-black focus:bg-white"
                />
              </div>

              {/* Desktop Category Filter */}
              <div className="hidden md:flex flex-wrap gap-3">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-5 py-2.5 rounded-lg whitespace-nowrap font-semibold text-sm transition-all border-2 ${
                      selectedCategory === category
                        ? "bg-black text-white border-black"
                        : "bg-white text-gray-900 border-gray-200 hover:border-black"
                    }`}
                  >
                    {category === "all" ? "All Items" : category}
                  </button>
                ))}
              </div>

              {/* Mobile Category Dropdown */}
              <div className="md:hidden">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full py-3.5 px-4 border-2 border-gray-200 rounded-lg bg-gray-50 text-gray-900 font-semibold text-[15px] cursor-pointer outline-none transition-all focus:border-black focus:bg-white"
                >
                  {categories.map((category) => (
                    <option key={category} value={category}>
                      {category === "all" ? "All Items" : category}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Menu Items */}
      <div className="bg-gray-50 pb-4">
        <div className="max-w-screen-xl mx-auto px-2">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col gap-6">
              {Object.keys(filteredMenu).length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm text-center py-12 px-4">
                  <p className="text-gray-500 text-base">
                    No dishes found matching your search.
                  </p>
                </div>
              ) : (
                Object.keys(filteredMenu).map((category) => (
                  <div
                    key={category}
                    className="bg-white rounded-lg shadow-sm overflow-hidden"
                  >
                    {/* Category Header */}
                    <div className="bg-black px-5 py-3.5">
                      <h2
                        className="text-xl md:text-2xl font-bold text-white uppercase tracking-wide"
                        style={{
                          fontFamily: "Georgia, 'Times New Roman', serif",
                        }}
                      >
                        {category}
                      </h2>
                    </div>

                    {/* Items Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                      {filteredMenu[category].map((item, index) => (
                        <div
                          key={index}
                          className="grid grid-cols-[56px_1fr_auto] md:grid-cols-[100px_1fr_auto]
      items-center gap-2 md:gap-4
      p-2.5 md:p-5
      border-b md:border-r last:border-b-0
      md:odd:border-r md:even:border-r-0
      md:[&:nth-last-child(-n+2)]:border-b-0
      border-gray-100 hover:bg-orange-50 transition-colors"
                        >
                          {/* Image */}
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-14 h-14 md:w-[100px] md:h-[100px]
        object-cover rounded-full border border-gray-100"
                          />

                          {/* Name */}
                          <div className="flex items-center gap-1.5 md:gap-2.5 min-w-0">
                            <span className="text-yellow-500 text-sm md:text-xl flex-shrink-0">
                              ★
                            </span>
                            <h3 className="font-medium text-gray-900 text-sm md:text-lg leading-snug">
                              {item.name}
                            </h3>
                          </div>

                          {/* Prices */}
                          <div className="flex flex-col items-end gap-1 flex-shrink-0">
                            {item.half && (
                              <div className="flex items-center gap-1.5">
                                <span
                                  className="bg-gray-100 px-2 py-0.5 md:px-3 md:py-1.5
              rounded text-[10px] md:text-[13px]
              font-semibold text-gray-600 min-w-[36px] text-center"
                                >
                                  Half
                                </span>
                                <span className="font-bold text-red-600 text-base md:text-[22px]">
                                  {item.half}
                                </span>
                              </div>
                            )}

                            {item.full && (
                              <div className="flex items-center gap-1.5">
                                <span
                                  className="bg-gray-100 px-2 py-0.5 md:px-3 md:py-1.5
              rounded text-[10px] md:text-[13px]
              font-semibold text-gray-600 min-w-[36px] text-center"
                                >
                                  Full
                                </span>
                                <span className="font-bold text-red-600 text-base md:text-[22px]">
                                  {item.full}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white pt-10 pb-4">
        <div className="max-w-screen-xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {/* About Section */}
            <div className="flex flex-col gap-3">
              <h3
                className="text-base font-bold text-white mb-2"
                style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
              >
                About Veer Ji
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Veer Ji Malai Chaap Wale is Ghaziabad's premier destination for
                authentic vegetarian cuisine. Serving the finest malai chaap and
                pure veg dishes.
              </p>
            </div>

            {/* Locations Section */}
            <div className="flex flex-col gap-3">
              <h3
                className="text-base font-bold text-white mb-2"
                style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
              >
                Our Locations
              </h3>
              <div className="flex flex-col gap-2">
                <span className="text-gray-400 text-sm">
                  📍 Kanha Complex Sec-2 Rajender Nagar
                </span>
                <span className="text-gray-400 text-sm">
                  📍 Karhera Colony Near Air Force Hindon
                </span>
                <span className="text-gray-400 text-sm">
                  📞 8860866540 | 9643580306
                </span>
              </div>
            </div>

            {/* Quick Links Section */}
            <div className="flex flex-col gap-3">
              <h3
                className="text-base font-bold text-white mb-2"
                style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
              >
                Quick Links
              </h3>
              <div className="flex flex-col gap-2">
                <a
                  href="#menu"
                  className="text-gray-400 text-sm hover:text-white transition-colors"
                >
                  Our Menu
                </a>
                <a
                  href="#delivery"
                  className="text-gray-400 text-sm hover:text-white transition-colors"
                >
                  Delivery Info
                </a>
                <a
                  href="#contact"
                  className="text-gray-400 text-sm hover:text-white transition-colors"
                >
                  Contact Us
                </a>
                <a
                  href="#about"
                  className="text-gray-400 text-sm hover:text-white transition-colors"
                >
                  About Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* SEO Footer */}
      <div className="bg-slate-900 border-t border-gray-800 py-8 pb-4">
        <div className="max-w-screen-xl mx-auto px-4">
          <div className="text-center">
            <h2
              className="text-lg md:text-xl font-bold text-white mb-4"
              style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
            >
              Best Vegetarian Restaurant in Ghaziabad | Veer Ji Malai Chaap Wale
            </h2>
            <p className="text-gray-400 text-[13px] leading-relaxed max-w-4xl mx-auto mb-6">
              Looking for the best malai chaap in Ghaziabad? Veer Ji Malai Chaap
              Wale serves authentic North Indian vegetarian cuisine in Mohan
              Nagar, Rajender Nagar, Hindon, Shahibabad, and surrounding areas.
              We specialize in premium malai chaap, tandoori dishes, and pure
              vegetarian food with free home delivery across Ghaziabad, UP.
              Experience the finest chaap restaurant near you with our signature
              recipes and fresh ingredients. Order now for the most delicious
              veg food in Ghaziabad!
            </p>

            {/* Keywords */}
            <div className="flex flex-wrap gap-2 justify-center mb-6">
              {[
                "Veer Ji Malai Chaap Wale",
                "Best Chaap in Ghaziabad",
                "Mohan Nagar Restaurant",
                "Rajender Nagar Food",
                "Hindon Veg Restaurant",
                "Shahibabad Chaap",
                "Pure Veg Restaurant Ghaziabad",
                "Malai Chaap Near Me",
                "Ghaziabad UP Restaurant",
                "Home Delivery Ghaziabad",
                "Best Veg Food Ghaziabad",
                "Tandoori Chaap",
                "North Indian Veg",
                "Karhera Colony Restaurant",
              ].map((keyword, index) => (
                <span
                  key={index}
                  className="bg-gray-800 text-gray-400 px-3 py-1.5 rounded text-xs border border-gray-700"
                >
                  {keyword}
                </span>
              ))}
            </div>

            {/* Copyright */}
            <div className="mt-6 pt-5 border-t border-gray-800">
              <p className="text-gray-500 text-xs">
                © 2025 Veer Ji - Malai Chaap Wale, Ghaziabad. All rights
                reserved. Serving Mohan Nagar, Rajender Nagar, Hindon,
                Shahibabad & all across Ghaziabad, UP.
              </p>
              <br />
              <p className="text-gray-500 text-xs">
                Developed By Nihal Singh <br></br>
                Contact to build Powerful Websites like this <br></br>
                Contact@8826597108
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VeerJiMenu;
